import moment from "moment";

export const capitalizeString = (str) => {
  return str ? str[0]?.toUpperCase() + str.slice(1) : "";
};

export const routeConvertor = {
  alert: "alert-system",
  mybot: "bot-automation",
  myais: "ai-assistant",
  integ: "integration",
  profl: "profile/info",
  dashb: "dashboard",
  insai: "Insight",
  myapi: "developer-api",
  price: "pricing-billing",
  dashm: "mini-dashboard",
  signl: "mini-ai-signals",
  mnbot: "mini-bots",
  plnrf: "mini-pricing-plan",
  mnptf: "mini-portfolio",
  mnset: "mini-settings",
};

export const getRelativeTime = (date, shortenSuffix = false) => {
  if (!date) return "";

  let targetDate = moment(date);

  // Convert to local time if it's in UTC
  if (moment.utc(date).isValid() && date.endsWith("Z")) {
    targetDate = moment.utc(date).local();
  }

  targetDate = moment.utc(date).local();

  const now = moment();
  const duration = moment.duration(now.diff(targetDate));

  if (duration.years() > 0) {
    return `${duration.years()} ${shortenSuffix ? "y" : `year${duration.years() > 1 ? "s" : ""}`} ago`;
  } else if (duration.months() > 0) {
    return `${duration.months()} ${shortenSuffix ? "M" : `month${duration.months() > 1 ? "s" : ""}`} ago`;
  } else if (duration.weeks() > 0) {
    return `${duration.weeks()} ${shortenSuffix ? "w" : `week${duration.weeks() > 1 ? "s" : ""}`} ago`;
  } else if (duration.days() > 0) {
    return `${duration.days()} ${shortenSuffix ? "d" : `day${duration.days() > 1 ? "s" : ""}`} ago`;
  } else if (duration.hours() > 0) {
    return `${duration.hours()} ${shortenSuffix ? "h" : `hour${duration.hours() > 1 ? "s" : ""}`} ago`;
  } else if (duration.minutes() > 0) {
    return `${duration.minutes()} ${shortenSuffix ? "m" : `minute${duration.minutes() > 1 ? "s" : ""}`} ago`;
  } else {
    return `${duration.seconds()} ${shortenSuffix ? "s" : `second${duration.seconds() > 1 ? "s" : ""}`} ago`;
  }
};

const calcRemainingTime = (date) => {
  const now = moment();
  const targetDate = moment(date);
  return moment.duration(targetDate.diff(now));
};

export const getCustomRemainingTime = (date, suffix, prefix) => {
  const duration = calcRemainingTime(new Date(date));

  if (duration.years() > 0) {
    return `${prefix ? `${prefix} ` : ""}${duration.years()} year${
      duration.years() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  } else if (duration.months() > 0) {
    return `${prefix ? `${prefix} ` : ""}${duration.months()} month${
      duration.months() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  } else if (duration.weeks() > 0) {
    return `${prefix ? `${prefix} ` : ""}${duration.weeks()} week${
      duration.weeks() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  } else if (duration.days() > 0) {
    return `${prefix ? `${prefix} ` : ""}${duration.days()} day${
      duration.days() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  } else if (duration.hours() > 0) {
    return `${prefix ? `${prefix} ` : ""}${duration.hours()} hour${
      duration.hours() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  } else if (duration.minutes() > 0) {
    return `${prefix ? `${prefix} ` : ""}${duration.minutes()} minute${
      duration.minutes() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  } else {
    return `${prefix ? `${prefix} ` : ""}${duration.seconds()} second${
      duration.seconds() > 1 ? "s" : ""
    }${suffix ? ` ${suffix}` : ""}`;
  }
};

export const getRemainingTime = (date, showExpired, showRemained = true) => {
  const duration = calcRemainingTime(date);

  if (duration.asSeconds() <= 0 && showExpired) {
    return "Expired";
  }

  if (duration.years() > 0) {
    return `${duration.years()} year${duration.years() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  } else if (duration.months() > 0) {
    return `${duration.months()} month${duration.months() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  } else if (duration.weeks() > 0) {
    return `${duration.weeks()} week${duration.weeks() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  } else if (duration.days() > 0) {
    return `${duration.days()} day${duration.days() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  } else if (duration.hours() > 0) {
    return `${duration.hours()} hour${duration.hours() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  } else if (duration.minutes() > 0) {
    return `${duration.minutes()} minute${duration.minutes() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  } else {
    return `${duration.seconds()} second${duration.seconds() > 1 ? "s" : ""}${
      showRemained ? " remaining" : " later"
    }`;
  }
};

export const formattedSubscription = {
  monthly: "Monthly Basic",
  yearly: "Yearly Basic",
  daily: "Daily Basic",
  monthly_pro: "Monthly Pro",
  daily_pro: "Daily Pro",
  yearly_pro: "Yearly Pro",
};

export const formatString = (str) => {
  if (str)
    return str
      .split("_") // Split the string by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(", "); // Join them with a comma and space
};

export const formatAIMode = {
  default: "All data sources",
  only_ai: "Only AI",
  only_onchain: "Only on-chain",
  only_ta: "Only technical indicator",
  only_social: "Only social",
};

export const returnDataForCreateBot = (basicData) => {
  const {
    bot_name,
    exchange_id: selected_exchange,
    signal_mode: signal_source,
    ai_assistant_mode,
    number_of_coins,
  } = basicData;

  return {
    bot_name,
    selected_exchange,
    trade_in_virtual_portfolio: "False",
    risk_tolerance: "3",
    target_return_percentage: "10",
    crypto_allocation_strategy: "balanced",
    signal_source,
    ai_assistant_mode,
    selected_trading_strategies: ["Time-based AI-assisted"],
    number_of_coins,
  };
};

export const minimizeText = (text, customLength = 15) => {
  if (text.length > customLength) {
    return text.slice(0, customLength) + "...";
  } else {
    return text;
  }
};

export const formatTelegramLog = (requestType, payload, url) => {
  return JSON.stringify({ requestType, payload, url });
};

export const alertFrequencyConverter = {
  "1H": "1 hour",
  "3H": "3 hours",
  "6H": "6 hours",
  "12H": "12 hours",
  "1D": "day",
  "4D": "twice a week",
  "7D": "weekly",
  "14D": "bi-weekly",
  "28D": "monthly",
};
